import {
  CONTROL_DOMAINS,
  IndustryProfileDefinition,
  INDUSTRY_MULTIPLIERS,
  PROFILE_DEPTHS,
  profileChanges,
  SCORE_BANDS,
  type IndustryProfileInput,
  type ProfileDepth,
  type ProfileKillSwitch,
} from '@cyberscore/contracts';
import { BadgeCheck, ListChecks, Plus, ShieldAlert, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { bandLabel } from '../portfolio/format';
import { useItems } from './api';
import { useItemDraft } from './draft';
import type { Issue } from './editors';
import { EditorHead, fromList, IssueList, toList } from './EditorParts';

type ProfileDraft = IndustryProfileInput;

const FIELD: Record<string, string> = { review: 'Review before cover', name: 'Name', journey: 'Journey', depth: 'Depth', larger: 'Larger businesses', multiplier: 'Multiplier', minimum_band: 'Minimum band', domain_weights: 'Domain weights', kill_switches: 'Kill switches' }; // prettier-ignore
const fieldLabel = (path: string) => {
  const [head, i] = path.split('.');
  if (head === 'kill_switches' && i !== undefined) return `Kill switch ${Number(i) + 1}`;
  return FIELD[head ?? ''] ?? (path || 'Profile');
};

export function profileIssues(p: ProfileDraft): Issue[] {
  const r = IndustryProfileDefinition.safeParse(p);
  const out: Issue[] = r.success
    ? []
    : r.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }));
  const seen = new Set<string>();
  (p.kill_switches ?? []).forEach((k, i) => {
    if (!k.control) out.push({ path: `kill_switches.${i}`, message: 'choose a control' });
    else if (seen.has(k.control))
      out.push({ path: `kill_switches.${i}`, message: `${k.control} is listed twice` });
    seen.add(k.control);
  });
  return out;
}

/**
 * An industry profile (task 79): the journey and depth an industry gets, how its score is weighed, the
 * controls that cap it, and the lowest band the insurer accepts. Platform profiles are customised by
 * insurers like any other item (their own appetite).
 */
export function ProfileEditor({ id }: { id: string }) {
  const d = useItemDraft<ProfileDraft>(id, href.profile);
  const journeys = useItems('journey');
  const controls = useItems('control');
  const [customise, setCustomise] = useState(false);

  if (d.item.isPending || !d.draft) return <div className="empty">Loading…</div>;
  if (d.item.isError) return <div className="empty">{d.item.error.message}</div>;

  const p = d.draft;
  const off = !d.owns;
  const issues = profileIssues(p);
  const set = (patch: Partial<ProfileDraft>) => d.setDraft((x) => ({ ...x, ...patch }));
  const industry = d.item.data!.key;
  const modelMultiplier = INDUSTRY_MULTIPLIERS[industry as keyof typeof INDUSTRY_MULTIPLIERS];
  const weights = p.domain_weights ?? {};
  const weightOf = (code: (typeof CONTROL_DOMAINS)[number]['code']) => weights[code] ?? CONTROL_DOMAINS.find((x) => x.code === code)!.weight; // prettier-ignore
  const total = CONTROL_DOMAINS.reduce((s, x) => s + weightOf(x.code), 0);
  const setWeight = (code: string, v: string) => {
    const next: Record<string, number> = { ...weights };
    if (v === '') delete next[code];
    else next[code] = Number(v);
    set({ domain_weights: next });
  };
  const kills = p.kill_switches ?? [];
  // What differs from the model, in plain words; the detail waits behind "Customise" (task 113).
  const changes = profileChanges(p, modelMultiplier);
  // The platform's own profile is the recommendation; a tenant's copy says what it changes.
  const platform = d.item.data!.scope_level === 'platform';
  const setReview = (patch: Partial<NonNullable<ProfileDraft['review']>>) =>
    set({ review: { ...p.review, ...patch } });
  // An empty specialist name removes the step.
  const setSpecialist = (patch: { label?: string; controls?: string[] }) => {
    const next = {
      label: p.review?.specialist?.label ?? '',
      controls: p.review?.specialist?.controls ?? [],
      ...patch,
    };
    setReview({ specialist: next.label.trim() || next.controls.length ? next : undefined });
  };
  const setKill = (i: number, patch: Partial<ProfileKillSwitch>) =>
    set({ kill_switches: kills.map((k, n) => (n === i ? { ...k, ...patch } : k)) });
  const controlKeys = [...new Map((controls.data ?? []).map((c) => [c.key, c.title])).entries()].sort(([a], [b]) => a.localeCompare(b)); // prettier-ignore
  const journeyKeys = [...new Map((journeys.data ?? []).map((j) => [j.key, j.title])).entries()];

  return (
    <>
      <EditorHead d={d} section="Industry profiles" sectionHref={href.studio()} title={p.name} blocked={issues.length > 0} />
      <div className="builder">
        <div className="stack">
          <section className="card pad">
            <div className="two">
              <div className="field">
                <label htmlFor="pe-name">Name</label>
                <input id="pe-name" className="input" disabled={off} value={p.name} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="pe-journey">Journey</label>
                <select id="pe-journey" className="input" disabled={off} value={p.journey ?? ''} onChange={(e) => set({ journey: e.target.value || undefined })}>
                  <option value="">The only one published</option>
                  {journeyKeys.map(([k, t]) => <option key={k} value={k}>{t || k}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="pe-desc">Description</label>
              <textarea id="pe-desc" className="input" rows={2} disabled={off} value={p.description ?? ''} onChange={(e) => set({ description: e.target.value })} />
            </div>
          </section>

          <section className={`card pad recommended${changes.length && !platform ? '' : ' ok'}`} aria-labelledby="pe-summary">
            <h2 id="pe-summary" className="section-title row gap">
              {changes.length && !platform ? <SlidersHorizontal size={16} /> : <BadgeCheck size={16} />}
              {platform ? `Recommended for ${p.name.toLowerCase()}` : changes.length ? `${changes.length === 1 ? 'One change' : `${changes.length} changes`} from the CyberScore model` : 'Using recommended settings'}
            </h2>
            {platform && <p className="muted small">The platform’s settings for this industry, beyond the general model. Customise them for your organisation only where your appetite differs.</p>}
            {changes.length ? (
              <ul className="small plain-bullets">{changes.map((c) => <li key={c}>{c}</li>)}</ul>
            ) : platform ? null : (
              <p className="muted small">The CyberScore model’s depth, weights, kill switches and review steps, which suit most insurers. Change them only where your appetite differs.</p>
            )}
          </section>

          <details className="customise" open={customise} onToggle={(e) => setCustomise(e.currentTarget.open)}>
          <summary className="btn">Customise these settings</summary>
          <div className="stack mt">
          <section className="card pad" aria-labelledby="pe-how">
            <h2 id="pe-how" className="section-title">How deep, and what passes</h2>
            <div className="three">
              <div className="field">
                <label htmlFor="pe-depth">Depth</label>
                <select id="pe-depth" className="input" disabled={off} value={p.depth ?? ''} onChange={(e) => set({ depth: (e.target.value || undefined) as ProfileDraft['depth'] })}>
                  <option value="">Each journey’s own target</option>
                  {Object.entries(PROFILE_DEPTHS).map(([k, v]) => <option key={k} value={k}>{v.label}: {v.help}</option>)}
                </select>
                <span className="hint">How much must be answered for a reliable score.</span>
              </div>
              <div className="field">
                <label htmlFor="pe-mult">Inherent risk multiplier</label>
                <input id="pe-mult" className="input num" type="number" step={0.05} min={0.5} max={3} disabled={off} value={p.multiplier ?? ''} placeholder={modelMultiplier ? String(modelMultiplier) : '1'} onChange={(e) => set({ multiplier: e.target.value === '' ? undefined : Number(e.target.value) })} />
                <span className="hint">Empty: the model’s ({modelMultiplier ?? 1}).</span>
              </div>
              <div className="field">
                <label htmlFor="pe-band">Minimum band</label>
                <select id="pe-band" className="input" disabled={off} value={p.minimum_band ?? ''} onChange={(e) => set({ minimum_band: (e.target.value || undefined) as ProfileDraft['minimum_band'] })}>
                  <option value="">None</option>
                  {SCORE_BANDS.map((b) => <option key={b.key} value={b.key}>{bandLabel(b.key)} ({b.min}+)</option>)}
                </select>
                <span className="hint">Below it, the assessment is referred.</span>
              </div>
            </div>
            <div className="two">
              <div className="field">
                <label htmlFor="pe-larger-staff">Larger businesses, from this many staff</label>
                <input id="pe-larger-staff" className="input num" type="number" min={2} disabled={off} value={p.larger?.staff ?? ''} placeholder="No size rule" onChange={(e) => set({ larger: e.target.value === '' ? undefined : { depth: p.larger?.depth ?? 'thorough', staff: Number(e.target.value) } })} />
              </div>
              <div className="field">
                <label htmlFor="pe-larger-depth">go this deep</label>
                <select id="pe-larger-depth" className="input" disabled={off || !p.larger} value={p.larger?.depth ?? 'thorough'} onChange={(e) => p.larger && set({ larger: { ...p.larger, depth: e.target.value as ProfileDepth } })}>
                  {Object.entries(PROFILE_DEPTHS).map(([k, v]) => <option key={k} value={k}>{v.label}: {v.help}</option>)}
                </select>
              </div>
            </div>
          </section>

          <section className="card pad" aria-labelledby="pe-weights">
            <h2 id="pe-weights" className="section-title">Domain weights</h2>
            <p className="muted small">Empty uses the model’s weight. Weights are relative: each domain’s share is its weight over the total.</p>
            <div className="table-wrap">
              <table className="grid" aria-label="Domain weights">
                <thead><tr><th>Domain</th><th className="num">Model</th><th className="num">This industry</th><th className="num">Share</th></tr></thead>
                <tbody>
                  {CONTROL_DOMAINS.map((x) => (
                    <tr key={x.code}>
                      <td><span className="mono small">{x.code}</span> {x.name}</td>
                      <td className="num muted">{x.weight}</td>
                      <td className="num">
                        <input className="input num weight-input" type="number" min={1} max={40} disabled={off} aria-label={`Weight of ${x.name}`} value={weights[x.code] ?? ''} placeholder={String(x.weight)} onChange={(e) => setWeight(x.code, e.target.value)} />
                      </td>
                      <td className={`num${weights[x.code] !== undefined ? ' strong' : ' muted'}`}>{Math.round((weightOf(x.code) / total) * 1000) / 10}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card pad" aria-labelledby="pe-review">
            <h2 id="pe-review" className="section-title row gap"><ListChecks size={16} /> Review before cover</h2>
            <p className="muted small">Steps the underwriter takes before deciding. A referral never waits for them.</p>
            <div className="two">
              <div className="field">
                <label htmlFor="pe-signoffs">Sign-off</label>
                <select id="pe-signoffs" className="input" disabled={off} value={p.review?.sign_offs ?? 1} onChange={(e) => setReview({ sign_offs: Number(e.target.value) })}>
                  <option value={1}>One underwriter decides</option>
                  <option value={2}>Two: one proposes, another confirms</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="pe-evidence">Evidence before accepting</label>
                <input id="pe-evidence" className="input mono" disabled={off} value={toList(p.review?.evidence_before_accept)} placeholder="e.g. DP-14, BR-02" onChange={(e) => setReview({ evidence_before_accept: fromList(e.target.value) })} />
                <span className="hint">Controls that must rest on more than the business’s word.</span>
              </div>
            </div>
            <div className="two">
              <div className="field">
                <label htmlFor="pe-spec-label">Specialist</label>
                <input id="pe-spec-label" className="input" disabled={off} value={p.review?.specialist?.label ?? ''} placeholder="No specialist review" onChange={(e) => setSpecialist({ label: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="pe-spec-controls">Reviews when not in place</label>
                <input id="pe-spec-controls" className="input mono" disabled={off || !p.review?.specialist} value={toList(p.review?.specialist?.controls)} placeholder="e.g. NS-17, IA-23" onChange={(e) => setSpecialist({ controls: fromList(e.target.value) })} />
              </div>
            </div>
          </section>

          <section className="card pad" aria-labelledby="pe-kills">
            <h2 id="pe-kills" className="section-title row gap"><ShieldAlert size={16} /> Kill switches of this industry</h2>
            <p className="muted small">Controls that cap the score in this industry, on top of the model’s, or a different cap for one of them. Its question is asked first and followed up when the answer is weak.</p>
            {kills.length === 0 && <div className="empty compact">Only the model’s kill switches.</div>}
            <ol className="kill-list" aria-label="Kill switches">
              {kills.map((k, i) => (
                <li key={i} className="kill-row" aria-label={`Kill switch ${i + 1}`}>
                  <select className="input" disabled={off} aria-label="Control" value={k.control} onChange={(e) => setKill(i, { control: e.target.value })}>
                    <option value="">Choose a control…</option>
                    {controlKeys.map(([key, t]) => <option key={key} value={key}>{key} · {t}</option>)}
                  </select>
                  <input className="input num" type="number" min={300} max={849} disabled={off} aria-label="Caps the score at" value={k.cap} onChange={(e) => setKill(i, { cap: Number(e.target.value) })} />
                  <select className="input" disabled={off} aria-label="Severity" value={k.severity} onChange={(e) => setKill(i, { severity: e.target.value as ProfileKillSwitch['severity'] })}>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                  </select>
                  {!off && (
                    <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove kill switch ${i + 1}`} onClick={() => set({ kill_switches: kills.filter((_, n) => n !== i) })}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ol>
            {!off && (
              <button type="button" className="btn mt" onClick={() => set({ kill_switches: [...kills, { control: '', cap: 649, severity: 'high' }] })}>
                <Plus size={16} /> Add a kill switch
              </button>
            )}
          </section>
          </div>
          </details>
        </div>
        <aside className="stack">
          <IssueList issues={issues} label={fieldLabel} />
          <section className="card pad">
            <div className="eyebrow">Applies to</div>
            <p className="small">Every assessment whose industry is <b className="mono">{industry}</b>, from the next release.</p>
          </section>
        </aside>
      </div>
    </>
  ); // prettier-ignore
}
